import { pluralize } from '@gonfalon/format/pluralize';
import { pluralizeWord } from '@gonfalon/format/pluralizeWord';

import { LimitNames } from '../../types';

/**
 * Example usage:
 * getLimitDisplayName(LimitNames.HOSTS) -> 'service connections'
 * getLimitDisplayName(LimitNames.HOSTS, 'plural') -> 'service connections'
 * getLimitDisplayName(LimitNames.HOSTS, 'titleName') -> 'Service connections'
 */
export function getLimitDisplayName(limitName: LimitNames, display: keyof LimitNameInfo = 'displayName'): string {
  const limitInfo = LIMIT_NAME_INFO[limitName];
  if (limitInfo === undefined) {
    return 'unknown';
  }
  return display === 'longTitleName'
    ? (limitInfo.longTitleName ?? limitInfo.titleName)
    : (limitInfo[display] ?? limitInfo.displayName);
}

/**
 * Returns the summary-card tooltip for a limit, or undefined when none is configured.
 * Unlike `getLimitDisplayName`, this does not fall back to another field — absence means
 * "no tooltip", so the summary card title renders without one.
 */
export function getLimitSummaryTooltip(limitName: LimitNames): string | undefined {
  return LIMIT_NAME_INFO[limitName]?.summaryTooltip;
}

export function pluralizeAbbreviatedName(abbreviatedName: string, value: number) {
  // We've standardized around MAU (as opposed to MAUs).
  // For now, service connections has a plural abbreviation of SCs.
  return abbreviatedName === 'MAU'
    ? abbreviatedName
    : pluralize(value, abbreviatedName, pluralizeWord(abbreviatedName));
}

export type LimitNameInfo = {
  titleName: string; // Limit title name
  longTitleName?: string; // Long limit title name when space allows
  summaryTooltip?: string; // Tooltip for the summary card title
  displayName: string; // Lowercase, singular default name
  plural: string; // Lowercase, plural name
  abbreviated: string; // Lowercase, singular, abbreviated name
};

type LimitNameInfoType = {
  [K in LimitNames]: LimitNameInfo;
};

/**
 * Maps each usage limit to its corresponding display names.
 *
 * Guidelines:
 * - Use `getLimitDisplayName` to get the display name for a limit.
 * - Prefer using CSS for handling uncommon capitalization cases instead of adding fields.
 * - Prefer referencing limits dynamically rather than hardcoding keys (e.g., LIMIT_NAME_INFO.Hosts).
 */
const LIMIT_NAME_INFO: LimitNameInfoType = {
  [LimitNames.MONTHLY_ACTIVE_USERS]: {
    titleName: 'Client-side MAU',
    displayName: 'client-side MAU',
    plural: 'client-side MAU',
    abbreviated: 'MAU',
  },
  [LimitNames.EVENTS_PUBLISHED]: {
    titleName: 'Streaming data export',
    displayName: 'streaming data export',
    plural: 'streaming data export events',
    abbreviated: 'event',
    summaryTooltip:
      'Events streamed to real-time destinations like Amazon Kinesis, Google Cloud Pub/Sub, Azure Event Hubs, mParticle, and Segment',
  },
  [LimitNames.EVENTS_RECEIVED]: {
    titleName: 'Experimentation events',
    displayName: 'experimentation event',
    plural: 'experimentation events',
    abbreviated: 'event',
  },
  [LimitNames.HOSTS]: {
    titleName: 'Service connections',
    displayName: 'service connection',
    plural: 'service connections',
    abbreviated: 'SC',
  },
  // Same as hosts, kept for backwards compatibility
  [LimitNames.SERVICE_CONNECTIONS]: {
    titleName: 'Service connections',
    displayName: 'service connection',
    plural: 'service connections',
    abbreviated: 'SC',
  },
  [LimitNames.SEATS]: {
    titleName: 'Seats',
    displayName: 'seat',
    plural: 'seats',
    abbreviated: 'seat',
  },
  [LimitNames.EXPERIMENTATION_KEYS]: {
    titleName: 'Experimentation keys',
    displayName: 'experimentation key',
    plural: 'experimentation keys',
    abbreviated: 'key',
  },
  [LimitNames.EXPERIMENTATION_MAU]: {
    titleName: 'Experimentation MAU',
    displayName: 'experimentation MAU',
    plural: 'experimentation MAU',
    abbreviated: 'MAU',
  },
  [LimitNames.SSO]: {
    titleName: 'SSO',
    displayName: 'SSO',
    plural: 'SSO',
    abbreviated: 'SSO',
  },
  [LimitNames.SESSIONS]: {
    titleName: 'Session replays',
    displayName: 'session replay',
    plural: 'session replays',
    abbreviated: 'session replay',
  },
  [LimitNames.ERRORS]: {
    titleName: 'Errors',
    displayName: 'error',
    plural: 'errors',
    abbreviated: 'error',
  },
  [LimitNames.LOGS]: {
    titleName: 'Logs',
    displayName: 'log',
    plural: 'logs',
    abbreviated: 'log',
  },
  [LimitNames.TRACES]: {
    titleName: 'Traces',
    displayName: 'trace',
    plural: 'traces',
    abbreviated: 'trace',
  },
  [LimitNames.AI_RUNS]: {
    titleName: 'AI runs',
    displayName: 'AI run',
    plural: 'AI runs',
    abbreviated: 'run',
  },
  [LimitNames.VEGA_AI]: {
    titleName: 'Vega',
    displayName: 'Vega prompt',
    plural: 'Vega prompts',
    abbreviated: 'prompt',
  },
};
